import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { PenaltyOverride, QuoteOverrides } from 'src/app/core/models/cancellation-overrides.model';
import { CommissionRefund, QuoteRequest } from 'src/app/core/models/itinerary.model';
import { ContractError } from 'src/app/core/models/models';
import { Step } from 'src/app/core/models/step.model';
import { CancellationActions } from '../actions';
import * as fromCancellation from '../reducers/cancellation.reducer';
import {
  CancellationConfirm,
  CancellationQuote,
  FeedbackModelML,
  PaymentChannel,
  PenaltyDetail,
  PenaltyFareBasis,
  PenaltyFareBasisMachineLearning
} from 'src/app/core/models/cancellation.model';

@Injectable()
export class CancellationService {
  quoteRequest: QuoteRequest;

  constructor(private store: Store<fromCancellation.CancellationState>) {}

  getCancellationQuote(): Observable<CancellationQuote> {
    return this.store.pipe(select(fromCancellation.getCancellationQuote));
  }

  getPenaltyFareBasis(): Observable<any> {
    return this.store.pipe(select(fromCancellation.getPenaltyFareBasis));
  }

  getPaymentChannel(): Observable<PaymentChannel> {
    return this.store.pipe(select(fromCancellation.getPaymentChannel));
  }

  getPaymentChannelByCountry(): Observable<PaymentChannel[]> {
    return this.store.pipe(select(fromCancellation.getPaymentChannelByCountry));
  }

  getOverrideError(): Observable<ContractError> {
    return this.store.pipe(select(fromCancellation.getError));
  }

  getSplitError(): Observable<ContractError> {
    return this.store.pipe(select(fromCancellation.getSplitError));
  }

  getCancellationError(): Observable<ContractError> {
    return this.store.pipe(select(fromCancellation.getCancellationError));
  }

  getMessageTypeStep2(): Observable<string> {
    return this.store.pipe(select(fromCancellation.getMessageTypeStep2));
  }

  getLoading(): Observable<boolean> {
    return this.store.pipe(select(fromCancellation.getLoading));
  }

  getLoadingEdit(): Observable<boolean> {
    return this.store.pipe(select(fromCancellation.getLoadingEdit));
  }

  getMessageTypePaymentChannel(): Observable<string> {
    return this.store.pipe(select(fromCancellation.getMessageTypePaymentChannel));
  }

  getSteps(): Observable<Step[]> {
    return this.store.pipe(select(fromCancellation.getSteps));
  }

  getActiveGdsRefundSwitch(): Observable<boolean> {
    return this.store.pipe(select(fromCancellation.getActiveGdsRefundSwitch));
  }

  getCancellationConfirm(): Observable<CancellationConfirm> {
    return this.store.pipe(select(fromCancellation.getCancellationConfirm));
  }

  getQuoteRequest(): Observable<QuoteRequest> {
    return this.store.pipe(select(fromCancellation.getQuoteRequest));
  }

  getCanGoToSecondStep(): Observable<boolean> {
    return this.store.pipe(select(fromCancellation.canGoToSecondStep));
  }

  dispatchChoosePaymentChannel(paymentChannel: PaymentChannel) {
    this.store.dispatch(CancellationActions.ChoosePaymentChannel({ paymentChannel }));
  }

  dispatchGoToStep1() {
    this.store.dispatch(CancellationActions.goToStep1());
  }

  dispatchGoToStep2() {
    this.store.dispatch(CancellationActions.goToStep2());
  }

  dispatchLoadCancellationSuccess(cancellationQuote: CancellationQuote) {
    this.store.dispatch(CancellationActions.LoadCancellationSuccess({ cancellationQuote }));
  }

  dispatchStorePenaltiesRequest(penaltyFareBasis: PenaltyFareBasis[]) {
    this.store.dispatch(CancellationActions.loadPenaltiesSuccess({ penaltyFareBasis }));
  }

  dispatchStorePenaltiesMLRequest(penaltyMLFareBasis: PenaltyFareBasisMachineLearning) {
    this.store.dispatch(CancellationActions.loadPenaltiesMLSuccess({ penaltyMLFareBasis }));
  }

  getPenaltiesMLFarebasis() {
    return this.store.pipe(select(fromCancellation.getPenaltyMLFareBasis));
  }

  dispatchSendFeedbackMLequest(feedback: FeedbackModelML) {
    this.store.dispatch(CancellationActions.sendFeedbackML({ feedback }));
  }

  dispatchStoreLoadingPenalties() {
    this.store.dispatch(CancellationActions.loadingPenaltiesRequest());
  }

  dispatchOverrideQuoteRequest(quoteOverrides: QuoteOverrides) {
    this.store.dispatch(CancellationActions.OverrideQuoteRequest({ quoteOverrides }));
  }

  dispatchCloseModal() {
    this.store.dispatch(CancellationActions.CloseModal());
  }

  dispatchAddCancellationCode(cancellationCode: string) {
    this.store.dispatch(CancellationActions.AddCancellationCode({ cancellationCode }));
  }

  dispatchStoreQuoteRequest(quoteRequest: QuoteRequest) {
    this.store.dispatch(CancellationActions.StoreQuoteRequest({ quoteRequest }));
  }

  dispatchActiveGdsRefundSwitch(activeGdsRefundSwitch: boolean) {
    this.store.dispatch(
      CancellationActions.ToggleIsGdsRefundSwitch({
        activeGdsRefundSwitch
      })
    );
  }

  obtainQuoteRequest(): QuoteRequest {
    this.getQuoteRequest()
      .pipe(take(1))
      .subscribe((response) => (this.quoteRequest = response));
    return this.quoteRequest;
  }

  dispatchCancelPnr() {
    this.obtainQuoteRequest();
    this.store.dispatch(
      CancellationActions.CancelPnrRequest({
        cancellationQuoteRequest: this.obtainQuoteRequest()
      })
    );
  }

  dispatchSplitPnr() {
    this.obtainQuoteRequest();
    this.store.dispatch(
      CancellationActions.SplitPnrRequest({
        quoteRequest: this.obtainQuoteRequest()
      })
    );
  }

  isOriginalPenalty(
    originalPenalties: PenaltyDetail[],
    passengerType: string,
    penalty: PenaltyOverride
  ): PenaltyOverride {
    originalPenalties.forEach((original) => {
      if (passengerType === original.passengerType) {
        penalty.isOriginal = true;
        penalty.originalValue = original.amount;
        penalty.value = original.amount.amount;
        penalty.currency = original.amount.currency;
      }
    });
    return penalty;
  }

  getPenaltiesOverride(): Observable<PenaltyOverride[]> {
    return this.store.pipe(select(fromCancellation.getPenaltiesOverride));
  }

  getPenaltiesAdditional(): Observable<PenaltyFareBasis[]> {
    return this.store.pipe(select(fromCancellation.getHighlightCategories));
  }

  getLoadingPenalties(): Observable<boolean> {
    return this.store.pipe(select(fromCancellation.loadingPenaltiesEffect));
  }

  dispatchAddCommissions(commissions: CommissionRefund[]) {
    this.store.dispatch(CancellationActions.AddCommissions({ commissions }));
  }

  dispatchErrorMachineLearningParameter() {
    this.store.dispatch(CancellationActions.errorLoadMachineLearningParameter());
  }

  validateMachineLearningModel(): Observable<boolean> {
    return this.store.pipe(select(fromCancellation.validateMachineLearningModel));
  }

  dispatchSetReceivedFrom(receivedFrom: string) {
    this.store.dispatch(CancellationActions.SetReceivedFrom({ receivedFrom }));
  }

  dispatchRemoveReceivedFrom(): void {
    this.store.dispatch(CancellationActions.RemoveReceivedFrom());
  }
}
